/**
 * Centralized access-policy hook.
 *
 * Single source of truth for all capability booleans consumed by screens
 * and components. Encodes the policy matrix defined in docs/access-policy.md.
 *
 * Usage:
 *   const { isMentor, canViewOwnProgress, hasProgressFeature } = useAccessPolicy();
 */
import { useAuth } from '../context/AuthContext';
import { hasEntitlement } from '../utils/billingAccess';
import {
  isSuperAdmin,
  isSchoolAdmin,
  isB2CUser,
  isB2B2CMentor,
  isB2B2CStudent,
  isSchoolManagedUser,
  isMentorOrAdmin,
  canShareDocuments,
  canAccessIndividualPaywall,
  canManageSchoolBilling,
  requiresIntakeCheck,
  canViewOwnProgress,
  canViewStudentProgress,
  canAccessAdminStudentList,
  canUploadMaterials,
  canAccessPlatformAdmin,
  canAccessDocumentsTab,
  getChatTitle,
} from '../utils/roleUtils';

export function useAccessPolicy() {
  const { user, billingStatus } = useAuth();
  const u = user as any;

  // ─── Identity booleans ───
  const isSuperAdminFlag = Boolean(u && isSuperAdmin(u));
  const isSchoolAdminFlag = Boolean(u && isSchoolAdmin(u));
  const isB2C = Boolean(u && isB2CUser(u));
  const isMentor = Boolean(u && isB2B2CMentor(u));
  const isStudent = Boolean(u && isB2B2CStudent(u));
  const isSchoolManaged = Boolean(u && isSchoolManagedUser(u));
  const isB2BUser = Boolean(u && u.school_id);

  // ─── Subscription state (separate from entitlement) ───
  const planTier = (billingStatus?.plan_tier ?? '').toLowerCase();
  const subStatus = (billingStatus?.subscription_status ?? '').toLowerCase();
  // School-course participants hold time-boxed tier3 access without a subscription.
  const isSubscriptionActive = ['active', 'trialing'].includes(subStatus)
    || Boolean(billingStatus?.course_access_active);
  const isTrialActive = Boolean(billingStatus?.free_trial_active);
  const isTier2OrHigher = ['tier2', 'tier3'].includes(planTier);

  // ─── Capabilities from policy matrix ───
  return {
    user: user ?? null,

    // Identity
    isSuperAdmin: isSuperAdminFlag,
    isSchoolAdmin: isSchoolAdminFlag,
    isB2C,
    isMentor,
    isStudent,
    isSchoolManaged,
    isB2BUser,

    // Subscription
    planTier,
    isSubscriptionActive,
    isTrialActive,

    // Policy capabilities
    canAccessIndividualPaywall: Boolean(u && canAccessIndividualPaywall(u)),
    canManageSchoolBilling: Boolean(u && canManageSchoolBilling(u)),
    requiresIntake: Boolean(u && requiresIntakeCheck(u)),
    canViewOwnProgress: Boolean(u && canViewOwnProgress(u)),
    canViewStudentProgress: Boolean(u && canViewStudentProgress(u)),
    canAccessAdminStudentList: Boolean(u && canAccessAdminStudentList(u)),
    canUploadMaterials: Boolean(u && canUploadMaterials(u)),
    canAccessPlatformAdmin: Boolean(u && canAccessPlatformAdmin(u)),
    canAccessDocumentsTab: Boolean(u && canAccessDocumentsTab(u)),
    canShare: Boolean(u && canShareDocuments(u)),
    isMentorOrAdmin: Boolean(u && isMentorOrAdmin(u)),

    // Chat
    chatTitle: u ? getChatTitle(u) : 'Chat',

    // Progress tab: tier-gated for B2C, always on for B2B, OFF for mentors.
    // The server's icf_progress entitlement also opens it (past_due grace,
    // tier3 course participants), never closes it.
    hasProgressFeature: Boolean(
      !isMentor && ( // mentors don't have own progress tab
        isSuperAdminFlag || isB2BUser ||
        isTrialActive ||
        (isSubscriptionActive && isTier2OrHigher) ||
        hasEntitlement(billingStatus, 'icf_progress', false)
      )
    ),
    canExportIcfPerformanceTranscript: Boolean(
      isSuperAdminFlag ||
      (isB2BUser && isTier2OrHigher) ||
      (isSubscriptionActive && isTier2OrHigher)
    ),
  };
}
